import { Subject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Inject, Injectable } from '@angular/core';

import { ServerApi } from '@services/server-api';
import { Session } from '@services/session';

import { extractResponseBody, IUTMParams } from '@lib/utils';
import { CookieKey } from '@lib/cookie-keys.const';
import { getCookie } from '@lib/cookies';

import {
  IGenericUpsellDetails,
  IGeneralUpsellParams,
  UPSELL_CAMPAIGNS_MAP,
  ISalesForceOpportunityParams,
  DEFAULT_UPSELL_PARAMS,
} from '@misc/upsell/upsell-config';

@Injectable()
export class FeatureUpgradeService {
  public featureModalHide$: Observable<void>;
  public featureModalUrlRedirect$: Observable<void>;
  public requestReceived$: Observable<boolean>;
  public ctaContentContext$: Observable<string>;
  private defaultUtmParams: IUTMParams = {
    utm_term: '',
    utm_medium: '',
    utm_source: '',
    utm_content: '',
    utm_campaign: '',
  };
  private featureModalHideSubject: Subject<void> = new Subject();
  private featureModalUrlRedirectSubject: Subject<void> = new Subject();
  private requestReceivedSubject: Subject<boolean> = new Subject<boolean>();
  private ctaContentContextSubject: Subject<string> = new Subject<string>();

  constructor(
    @Inject(ServerApi) private api,
    private session: Session,
  ) {
    this.featureModalHide$ = this.featureModalHideSubject.asObservable();
    this.featureModalUrlRedirect$ = this.featureModalUrlRedirectSubject.asObservable();
    this.requestReceived$ = this.requestReceivedSubject.asObservable();
    this.ctaContentContext$ = this.ctaContentContextSubject.asObservable();
  }

  public emitRequestReceivedEvent(event: boolean): void {
    this.requestReceivedSubject.next(event);
  }

  public emitCTAContentContextEvent(event: string): void {
    this.ctaContentContextSubject.next(event);
  }

  public getTrialFeature(companyId: number, upsellKey: string): any {
    return this.api
      .put(`companies/${companyId}/enable_dashboard_upsell_pro_trial`, { upsell_product_interest: upsellKey })
      .pipe(
        map(extractResponseBody),
      )
    ;
  }

  public generalUpgrade(upsellParams: IGeneralUpsellParams): Observable<any> {

    // remove keys with empty data (e.g. product_interest or comments)
    const params: any = this.removeEmpty({
      campaign_type: upsellParams.subscribeTo,
      email: upsellParams.userEmail,
      first_name: upsellParams.firstName,
      last_name: upsellParams.lastName,
      company_name: upsellParams.companyName,
      product_interest: upsellParams.productInterest || '',
      comments: upsellParams.comments || '',
      upsell_product_interest: upsellParams.upsellKey || '',
      cta_content: upsellParams.ctaContent,
      fe_test_info: upsellParams.feTestInfo || '',
    });

    return this.api
      .post('dashboard_upsell', params)
      .pipe(
        map(extractResponseBody),
      );
  }

  public pendoUpsell(upsellParams: IGenericUpsellDetails, overrideCookie: boolean = false): Observable<any> {
    const params: IGenericUpsellDetails = {
      ...this.getSanitizeUpsellParams(upsellParams),
      utm_params: overrideCookie ? upsellParams.utm_params : this.getUTMParams(upsellParams),
    };

    return this.api
      .post('dashboard_upsell/pendo', params)
      .pipe(
        map(extractResponseBody),
      );
  }

  // Adding a similar function to the one above
  // as overloading like this is not supported yet
  public genericUpsell(upsellCampaign: string): Observable<any> {
    const upsellParams: IGenericUpsellDetails = UPSELL_CAMPAIGNS_MAP[upsellCampaign];

    const params: IGenericUpsellDetails = {
      ...this.getSanitizeUpsellParams(upsellParams),
      utm_params: this.getUTMParams(upsellParams),
    };

    return this.api
      .post('dashboard_upsell/pendo', params)
      .pipe(
        map(extractResponseBody),
      );
  }

  public genericUpsellWithCustomProps(
    campaignName: string,
    ctaContent: string,
    offerType: string,
    utmContent: string,
    productInterest: string,
    urlOfMostRecentMstFill: string = '',
    utmSource: string = 'dashboard-app',
  ): Observable<any> {
    const upsellParams: IGenericUpsellDetails = DEFAULT_UPSELL_PARAMS;

    const params: IGenericUpsellDetails = {
      ...this.getSanitizeUpsellParams(upsellParams),
      utm_params: {
        ...upsellParams.utm_params,
      },
    };

    params.utm_params.utm_campaign = campaignName;
    params.cta_content = ctaContent;
    params.offer_type = offerType;
    params.utm_params.utm_content = utmContent;
    params.product_interest = productInterest;
    params.url_of_most_recent_mst_fill = urlOfMostRecentMstFill;
    params.utm_params.utm_source = utmSource;

    return this.api
      .post('dashboard_upsell/pendo', params)
      .pipe(
        map(extractResponseBody),
      );
  }

  public createSalesForceOpportunity(upsellCampaign: string): Observable<any> {
    const upsellParams: IGenericUpsellDetails = UPSELL_CAMPAIGNS_MAP[upsellCampaign];

    const params: ISalesForceOpportunityParams = {
      user_kt_id: this.session.getUserId(),
      ...this.getUTMParams(upsellParams),
    };

    return this.api
      .post('salesforce_opportunity/create', params, null, 'w1')
      .pipe(
        map(extractResponseBody),
      );
  }

  public emitFeatureModalHiddenEvent(): void {
    this.featureModalHideSubject.next();
  }

  public emitFeatureModalUrlRedirectEvent(): void {
    this.featureModalUrlRedirectSubject.next();
  }

  private removeEmpty(obj) {
    return Object.entries(obj)
      .filter(([_, v]) => v != null)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
  }

  private getSanitizeUpsellParams(upsellParams: IGenericUpsellDetails): any {
    return {
      ...upsellParams,
      offer_type: upsellParams.offer_type || '',
      cta_content: upsellParams.cta_content || '',
      fe_test_info: upsellParams.fe_test_info || '',
      product_interest: upsellParams.product_interest || '',
      url_of_most_recent_mst_fill: upsellParams.url_of_most_recent_mst_fill || '',
      upsell_product_interest: upsellParams.upsell_product_interest || '',
    };
  }

  // give preference to values in utm_params cookie
  // if utm_params cookie is not available, then use the utm values sent as argument
  // if those aren't present, use empty string
  private getUTMParams(upsellParams: IGenericUpsellDetails): IUTMParams {
    const utmParams: IUTMParams = {
      ...this.defaultUtmParams,
      ...upsellParams.utm_params,
    };

    const UTM_PARAMS_COOKIE_VALUE: CookieKey = getCookie(CookieKey.UTM_PARAMS);

    if (UTM_PARAMS_COOKIE_VALUE) {
      const UTM_PARAMS_FROM_COOKIE: IUTMParams = JSON.parse(UTM_PARAMS_COOKIE_VALUE);

      utmParams.utm_term = UTM_PARAMS_FROM_COOKIE?.utm_term || utmParams.utm_term || '';
      utmParams.utm_medium = UTM_PARAMS_FROM_COOKIE?.utm_medium || utmParams.utm_medium || '';
      utmParams.utm_source = UTM_PARAMS_FROM_COOKIE?.utm_source || utmParams.utm_source || '';
      utmParams.utm_content = UTM_PARAMS_FROM_COOKIE?.utm_content || utmParams.utm_content || '';
      utmParams.utm_campaign = UTM_PARAMS_FROM_COOKIE?.utm_campaign || utmParams.utm_campaign || '';
    }

    return utmParams;
  }

  public getDefaultUTMParams(upsellCampaign: string): IUTMParams {
    const upsellParams: IGenericUpsellDetails = UPSELL_CAMPAIGNS_MAP[upsellCampaign];
    return {
      ...this.defaultUtmParams,
      ...upsellParams.utm_params,
    };
  }
}
